import { useGeneratedSalesPortalMenuImageItemsLazyQuery } from '@/experiences/sales-portal/blocs/documents/sales-portal-menu-image-items.generated';

export interface MenuImageItem {
  ingredients: string[];
  name: string;
}

interface MenuImageItemsLoaderResult {
  loadMenuItems: (imageUrls: string[]) => Promise<MenuImageItem[]>;
}

/**
 * Fetches the menu items the backend extracts from the given uploaded-image URLs.
 *
 * @returns `loadMenuItems`, a callback resolving to the extracted menu items for
 * the given image URLs (empty when the backend returns none)
 *
 * @example
 * ```ts
 * const { loadMenuItems } = useMenuImageItemsLoader();
 * const items = await loadMenuItems(uploadedImageUrls);
 * // items: [{ name: 'Margherita pizza', ingredients: ['flour', ...] }, ...]
 * ```
 */
export const useMenuImageItemsLoader = (): MenuImageItemsLoaderResult => {
  const getMenuImageItems = useGeneratedSalesPortalMenuImageItemsLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const loadMenuItems = async (
    imageUrls: string[],
  ): Promise<MenuImageItem[]> => {
    const { data } = await getMenuImageItems({ variables: { imageUrls } });
    return (
      data?.getItemsFromMenuImages.items.map((item) => ({
        ingredients: item.ingredients,
        name: item.item_name,
      })) ?? []
    );
  };

  return { loadMenuItems };
};
